/* Open Road -- product docket variant picker.
 *
 * Why this exists rather than Dawn's <product-info>: product-info.js drives
 * variant changes through the Section Rendering API and swaps DOM keyed to
 * main-product's contract -- media-gallery ul, product-modal, share-button,
 * pickup-availability, #Quantity-Rules-, #ProductSubmitButton-. The docket
 * has none of those (it uses open-road-pdp-gallery.js), so adopting it would
 * mean rebuilding the section to Dawn's markup and would also re-render the
 * docket out from under the gallery script on every change. This element does
 * the same job for the markup we actually have, client-side, with no fetch.
 *
 * Dawn's own <quantity-input> (global.js, loaded site-wide) is reused as-is --
 * it is self-contained and works with any input name, so the stepper needs no
 * code here beyond keeping min/max/step in step with the selected variant.
 *
 * Progressive enhancement: without JS the selects still render and the hidden
 * id input still carries the first available variant, so the page degrades to
 * exactly the behaviour it had before this element existed.
 *
 * Markup contract (see open-road-product-docket.liquid):
 *   <docket-variants data-update-url="true">
 *     select[data-option-position]         one per product option, NO name
 *                                          attribute -- the hidden id input is
 *                                          what gets submitted, not options[]
 *     script[data-docket-variants]         JSON array, money already formatted
 *                                          by Liquid so no currency logic here
 *   Reached on the section root (.or-product-docket):
 *     input.product-variant-id, .pdp-btn--buy, .docket__price, .docket__stamp,
 *     .quantity__input, [data-or-gallery-image-id] thumbnails
 */
(function () {
  if (window.customElements && customElements.get('docket-variants')) return;

  var SOLD_OUT_SUFFIX = ' \u2014 sold out';

  function DocketVariantsFactory() {
    class DocketVariants extends HTMLElement {
      connectedCallback() {
        this.root = this.closest('.or-product-docket');
        var json = this.querySelector('[data-docket-variants]');
        if (!this.root || !json) return;

        try {
          this.variants = JSON.parse(json.textContent);
        } catch (e) {
          return; /* Leave the form exactly as Liquid rendered it. */
        }
        if (!Array.isArray(this.variants) || !this.variants.length) return;

        this.selects = Array.prototype.slice.call(this.querySelectorAll('select[data-option-position]'));
        if (!this.selects.length) return;

        /* Options with a single value get no select (see the section's
           single-value skip), so the starting point is the variant Liquid
           rendered -- the selects then overwrite only their own positions. */
        try {
          this.initial = JSON.parse(this.dataset.initial);
        } catch (e) {
          this.initial = [];
        }
        if (!Array.isArray(this.initial)) this.initial = [];

        this.idInput = this.root.querySelector('.product-variant-id');
        this.button = this.root.querySelector('.pdp-btn--buy');
        this.buttonText = this.button ? this.button.querySelector('span') : null;
        this.priceEl = this.root.querySelector('.docket__price');
        this.stampEl = this.root.querySelector('.docket__stamp');
        this.qtyInput = this.root.querySelector('.quantity__input');
        /* Collection-only pages have no buy form; the chosen option travels in
           a hidden field on the enquiry form instead. */
        this.collectField = this.root.querySelector('[data-collect-variant]');
        this.thumbs = this.root.querySelectorAll('[data-or-gallery-image-id]');

        this.addEventListener('change', this.onChange.bind(this));
        this.markUnavailable();
      }

      onChange() {
        var variant = this.findVariant(this.selectedValues());
        this.markUnavailable();
        this.apply(variant);
      }

      selectedValues() {
        var values = this.initial.slice();
        this.selects.forEach(function (select) {
          values[parseInt(select.dataset.optionPosition, 10) - 1] = select.value;
        });
        return values;
      }

      findVariant(values) {
        /* every() walks variant.options, not values, so a values array that is
           short or sparse can never match by accident. */
        return (
          this.variants.filter(function (variant) {
            return variant.options.every(function (option, i) {
              return option === values[i];
            });
          })[0] || null
        );
      }

      /* Flag option values that cannot be bought given the OTHER selects'
         current values, so a shopper sees the dead end before choosing it. */
      markUnavailable() {
        var self = this;
        var current = this.selectedValues();

        this.selects.forEach(function (select) {
          var position = parseInt(select.dataset.optionPosition, 10) - 1;

          Array.prototype.forEach.call(select.options, function (option) {
            if (!option.dataset.label) option.dataset.label = option.textContent.trim();

            var probe = current.slice();
            probe[position] = option.value;

            var match = self.findVariant(probe);
            var buyable = !!match && match.available;

            option.toggleAttribute('data-unavailable', !buyable);
            option.textContent = buyable ? option.dataset.label : option.dataset.label + SOLD_OUT_SUFFIX;
          });
        });
      }

      apply(variant) {
        this.setForm(variant);
        this.setCollectField(variant);
        this.setPrice(variant);
        this.setStamp(variant);
        this.setQuantityRules(variant);
        this.setImage(variant);
        this.setUrl(variant);
      }

      setForm(variant) {
        if (this.idInput) {
          this.idInput.value = variant ? variant.id : '';
          this.idInput.disabled = !variant || !variant.available;
        }
        if (!this.button) return;

        this.button.disabled = !variant || !variant.available;
        if (!this.buttonText) return;

        if (!variant) this.buttonText.textContent = 'Unavailable';
        else if (!variant.available) this.buttonText.textContent = 'Sold out';
        else this.buttonText.textContent = 'Add to cart';
      }

      setCollectField(variant) {
        if (!this.collectField || !variant) return;
        this.collectField.value = variant.options.join(' / ');
      }

      setPrice(variant) {
        /* Money is pre-formatted by Liquid's money filter, so this never has
           to know the shop's currency or format. */
        if (this.priceEl && variant && variant.price) this.priceEl.textContent = variant.price;
      }

      setStamp(variant) {
        if (!this.stampEl) return;
        /* On a collection-only product the stamp reads "Collection only" and
           is not about stock at all, so it must not be rewritten here. */
        if (this.dataset.lockStamp === 'true') return;
        var available = !!variant && variant.available;
        this.stampEl.textContent = available ? 'In stock' : 'Sold out';
        this.stampEl.classList.toggle('docket__stamp--sold-out', !available);
      }

      setQuantityRules(variant) {
        if (!this.qtyInput || !variant) return;

        var min = variant.qmin || 1;
        this.qtyInput.min = min;
        this.qtyInput.dataset.min = min;
        this.qtyInput.step = variant.qstep || 1;

        if (variant.qmax === null || typeof variant.qmax === 'undefined') this.qtyInput.removeAttribute('max');
        else this.qtyInput.max = variant.qmax;

        var value = parseInt(this.qtyInput.value, 10) || min;
        if (value < min) value = min;
        if (variant.qmax && value > variant.qmax) value = variant.qmax;
        this.qtyInput.value = value;

        /* Dawn's QuantityInput re-runs validateQtyRules on 'change', which is
           what greys out the +/- buttons at the bounds. Cheaper and less
           brittle than reaching into the element or the pubsub bus. */
        this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      setImage(variant) {
        if (!variant || !variant.image || !this.thumbs.length) return;

        var target = this.root.querySelector('[data-or-gallery-image-id="' + variant.image + '"]');
        /* Click the thumbnail rather than swapping src directly, so the one
           gallery script stays the only thing that owns main-image state. */
        if (target && !target.classList.contains('is-on')) target.click();
      }

      setUrl(variant) {
        if (!variant || this.dataset.updateUrl === 'false') return;
        if (!window.history || !window.history.replaceState) return;

        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    customElements.define('docket-variants', DocketVariants);
  }

  DocketVariantsFactory();
})();
